import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaCheckCircle, FaSpinner } from 'react-icons/fa';
import {
  getResolutionTypeOptions,
  MAX_RESOLUTION_NOTES_LENGTH,
} from '../../../services/resolutionTypeService';

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
`;

const Modal = styled.div`
  background: var(--color-bg-primary);
  border-radius: 16px;
  padding: 1.5rem;
  width: min(92vw, 480px);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
  border: 1px solid var(--color-border-primary);
`;

const ModalTitle = styled.h3`
  margin: 0 0 0.35rem;
  font-size: 1.1rem;
  color: var(--color-text-primary);
  font-weight: 700;
`;

const ModalSubtitle = styled.p`
  margin: 0 0 1.25rem;
  font-size: 0.88rem;
  color: var(--color-text-secondary);
  line-height: 1.45;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1rem;
  font-size: 0.82rem;
  color: var(--color-text-primary);
  font-weight: 600;
`;

const Select = styled.select`
  width: 100%;
  box-sizing: border-box;
  padding: 0.65rem 0.75rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  font-size: 0.92rem;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);

  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    box-shadow: 0 0 0 3px var(--color-hover-overlay);
  }

  option {
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 110px;
  padding: 0.65rem 0.75rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  font-size: 0.92rem;
  resize: vertical;
  font-family: inherit;
  line-height: 1.45;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);

  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    box-shadow: 0 0 0 3px var(--color-hover-overlay);
  }
`;

const CharCount = styled.span`
  align-self: flex-end;
  font-size: 0.75rem;
  color: ${(props) => (props.$warn ? '#c0392b' : 'var(--color-text-muted)')};
  font-weight: 500;
`;

const ErrorText = styled.p`
  margin: -0.35rem 0 0.85rem;
  font-size: 0.8rem;
  color: #c0392b;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 0.25rem;
`;

const Btn = styled.button`
  border: none;
  border-radius: 10px;
  padding: 0.6rem 1rem;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    transform: translateY(-1px);
  }
`;

const CancelBtn = styled(Btn)`
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-primary);
`;

const ConfirmBtn = styled(Btn)`
  background: #1f4e79;
  color: #ffffff;
`;

const ResolveTicketModal = ({
  open,
  requireResolutionType = true,
  submitting = false,
  targetStatusLabel = '',
  onCancel,
  onConfirm,
}) => {
  const [resolutionType, setResolutionType] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setResolutionType('');
      setResolutionNotes('');
      setError('');
    }
  }, [open]);

  if (!open) return null;

  const options = getResolutionTypeOptions();
  const notesLength = resolutionNotes.length;
  const notesTooLong = notesLength > MAX_RESOLUTION_NOTES_LENGTH;
  const statusHint = targetStatusLabel
    ? ` ao mover para "${targetStatusLabel}"`
    : '';

  const handleConfirm = () => {
    if (requireResolutionType && !resolutionType) {
      setError('Selecione o tipo de resolução para continuar.');
      return;
    }
    if (notesTooLong) {
      setError(`As notas não podem exceder ${MAX_RESOLUTION_NOTES_LENGTH} caracteres.`);
      return;
    }
    setError('');
    onConfirm({
      resolutionType: resolutionType || null,
      resolutionNotes: resolutionNotes.trim() || null,
    });
  };

  return (
    <ModalBackdrop onClick={() => !submitting && onCancel()}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalTitle>Registrar resolução</ModalTitle>
        <ModalSubtitle>
          Informe como este ticket foi resolvido{statusHint}. As notas são opcionais e ficam registradas no histórico.
        </ModalSubtitle>

        <Field>
          Tipo de resolução{requireResolutionType ? ' *' : ''}
          <Select
            value={resolutionType}
            onChange={(e) => {
              setResolutionType(e.target.value);
              if (error) setError('');
            }}
            disabled={submitting}
            autoFocus
          >
            <option value="">Selecionar...</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          Notas adicionais (opcional)
          <Textarea
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            placeholder="Ex.: passos executados, causa raiz, links ou observações relevantes..."
            disabled={submitting}
            maxLength={MAX_RESOLUTION_NOTES_LENGTH + 50}
          />
          <CharCount $warn={notesTooLong}>
            {notesLength}/{MAX_RESOLUTION_NOTES_LENGTH}
          </CharCount>
        </Field>

        {error && <ErrorText>{error}</ErrorText>}

        <ModalActions>
          <CancelBtn type="button" onClick={onCancel} disabled={submitting}>
            Cancelar
          </CancelBtn>
          <ConfirmBtn type="button" onClick={handleConfirm} disabled={submitting}>
            {submitting ? <FaSpinner className="spin" /> : <FaCheckCircle />}
            Confirmar
          </ConfirmBtn>
        </ModalActions>
      </Modal>
    </ModalBackdrop>
  );
};

ResolveTicketModal.propTypes = {
  open: PropTypes.bool.isRequired,
  requireResolutionType: PropTypes.bool,
  submitting: PropTypes.bool,
  targetStatusLabel: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default ResolveTicketModal;
